import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Wraps the repeated list-page mutation skeleton: run a mutation, invalidate a
 * single React Query key on success, and re-throw on failure so the caller
 * (RowActions) can surface the error inline in its confirm Modal. Deliberately
 * has NO onError/window.alert — error presentation is the caller's concern.
 */
export function useConfirmedMutation<TInput = void>({
  mutationFn,
  invalidateKey,
}: {
  mutationFn: (input: TInput) => Promise<unknown>;
  invalidateKey: readonly unknown[];
}): { run: (input: TInput) => Promise<void>; isPending: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({ mutationFn });

  async function run(input: TInput): Promise<void> {
    await mutation.mutateAsync(input);
    await queryClient.invalidateQueries({ queryKey: invalidateKey });
  }

  return { run, isPending: mutation.isPending };
}
